import { debounce } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import { useBoolean } from 'src/hooks/use-boolean';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { SortOrder } from 'src/services/common.types';
import { SortConfig, PageOptions } from 'src/services/api.types';
import { openSingleMailerDialog } from 'src/store/slices/app/appSlice';
import { IContactListItem } from 'src/services/contacts/contacts.types';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { MailerTargetType, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';
import { useRemoveContactMutation, useGetOrganizationContactsLeanQuery } from 'src/services/contacts/contacts.service';

import Iconify from 'src/components/iconify';
import DataList from 'src/components/data-list/data-list';
import RecordPanel from 'src/components/record-panel/record-panel';
import { useRecordPanel } from 'src/components/record-panel/use-record-panel';
import DataListActionsMenu from 'src/components/data-list/data-list-actions-menu';
import DataListColumnPicker from 'src/components/data-list/data-list-column-picker';
import { useColumnPreferences } from 'src/components/data-list/use-column-preferences';
import { useListCustomFieldColumns } from 'src/components/data-list/use-list-custom-field-columns';

import { useCanImport } from 'src/sections/import/use-can-import';
import AddContactModal from 'src/sections/contacts/modal/add-contact-modal';
import { useContactActions } from 'src/sections/contacts/hooks/use-contact-actions';
import BulkContactUploadModal from 'src/sections/contacts/modal/bulk-contact-import-modal';

import ContactRowActions from './contact-row-actions';
import { buildContactColumns } from './contact-columns';

const PAGE_SIZE = 50;

/**
 * Contacts list on the shared DataList (JT1-1527/1528, item 5). Configurable/sortable columns from
 * the lean endpoint, per-column search, infinite scroll, and the contact actions (view, notes,
 * email, call/SMS, add task, remove) via ContactRowActions. Replaces the CONTACTS tab's old table.
 * The toolbar's actions on the ticked rows are the contact profile's full list (JT1-1799).
 */
export default function ContactsList() {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const { openRecord } = useRecordPanel();
  const authContext = useAuthContext();
  const orgId = authContext.organization?.id;

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<IContactListItem[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({ sort_by: 'last_activity_at', sort_order: SortOrder.DESC });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const pageOptions: PageOptions = useMemo(() => ({ page, per_page: PAGE_SIZE }), [page]);

  const { currentData, isFetching } = useGetOrganizationContactsLeanQuery(
    { org_id: orgId as string, search_query: filterQuery || undefined, column_filters: columnSearch, page: pageOptions, sort_config: sortConfig },
    { skip: !orgId, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (!currentData) return;
    setRows((prev) => {
      if (page === 1) return currentData.results;
      const seen = new Set(prev.map((r) => r.id));
      return [...prev, ...currentData.results.filter((r) => !seen.has(r.id))];
    });
  }, [currentData, page]);

  const count = currentData?.count ?? 0;
  const hasMore = rows.length < count;
  const isSearching = isFetching || searchInput !== filterQuery;

  const resetToFirstPage = useCallback(() => setPage(1), []);

  const applySearch = useMemo(() => debounce((value: string) => { setFilterQuery(value); resetToFirstPage(); }, 300), [resetToFirstPage]);
  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const onSearchChange = useCallback((value: string) => { setSearchInput(value); applySearch(value); }, [applySearch]);
  const onSortChange = useCallback((next: SortConfig) => { setSortConfig(next); resetToFirstPage(); }, [resetToFirstPage]);
  const onColumnSearchChange = useCallback((key: string, value: string) => {
    setColumnSearch((prev) => {
      const next = { ...prev };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
    resetToFirstPage();
  }, [resetToFirstPage]);
  const onLoadMore = useCallback(() => { if (!isFetching && hasMore) setPage((p) => p + 1); }, [isFetching, hasMore]);

  const onSelectRow = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);
  const onSelectAll = useCallback((checked: boolean, ids: string[]) => setSelectedIds(checked ? Array.from(new Set(ids)) : []), []);

  const addContact = useBoolean();
  const importContacts = useBoolean();
  const canImport = useCanImport();

  const [removeContact, { isLoading: isRemoving }] = useRemoveContactMutation();

  const handleRemove = useCallback(async (row: IContactListItem) => {
    try {
      await removeContact({ contact_id: row.id }).unwrap();
      enqueueSnackbar(t('contacts.api.delete.success'), { variant: 'success' });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch {
      enqueueSnackbar(t('contacts.api.delete.default_error'), { variant: 'error' });
    }
  }, [removeContact, t]);

  const handleSendEmail = useCallback((row: IContactListItem) => {
    if (!row.email) return;
    dispatch(openSingleMailerDialog({
      emails: [row.email],
      templateType: EmailMessageTemplateType.CONTACT_ENGAGEMENT,
      templateEntityId: row.id,
      targetType: MailerTargetType.CONTACT,
      targetId: row.id,
    }));
  }, [dispatch]);

  // Toolbar actions on the ticked rows: the contact profile's list. The one ticked row is passed in
  // from the lean row so ticking doesn't load the full record.
  const selectedRow = selectedIds.length === 1 ? rows.find((r) => r.id === selectedIds[0]) : undefined;
  const clearSelection = useCallback(() => setSelectedIds([]), []);
  const { actions: contactActions, modals: contactActionModals } = useContactActions({
    mode: 'selection',
    ids: selectedIds,
    target: selectedRow && {
      id: selectedRow.id,
      firstName: selectedRow.first_name,
      lastName: selectedRow.last_name,
      email: selectedRow.email,
      phone: selectedRow.phone,
    },
    onAddedToList: clearSelection,
  });
  const firstVisibleAction = contactActions.findIndex((a) => !a.hidden);

  const handleQuickView = useCallback((id: string) => openRecord('contact', id, { mode: 'quick' }), [openRecord]);
  const handleOpenOverlay = useCallback((id: string) => openRecord('contact', id, { mode: 'overlay' }), [openRecord]);

  const { cfColumns, isReady: cfReady } = useListCustomFieldColumns<IContactListItem>(CustomFieldEntityType.CONTACT);

  const registry = useMemo(
    () => buildContactColumns({ t, onOpenRecord: handleOpenOverlay, customFieldColumns: cfColumns }),
    [t, handleOpenOverlay, cfColumns]
  );
  const { columns, pickerModel, save } = useColumnPreferences('contacts', registry);

  const renderRowLeading = useCallback((row: IContactListItem) => (
    <Tooltip title={t('common.quick_view', { defaultValue: 'Quick view' })}>
      <IconButton size="small" onClick={() => handleQuickView(row.id)}>
        <Iconify icon="solar:eye-bold" />
      </IconButton>
    </Tooltip>
  ), [handleQuickView, t]);

  const renderRowActions = useCallback((row: IContactListItem) => (
    <ContactRowActions row={row} onView={handleQuickView} onSendEmail={handleSendEmail} onRemove={handleRemove} isRemoving={isRemoving} />
  ), [handleQuickView, handleSendEmail, handleRemove, isRemoving]);

  const toolbar = (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
      <TextField
        size="small"
        value={searchInput}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={t('contacts.table.search_placeholder', { defaultValue: 'Search contacts' })}
        InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment> }}
        sx={{ width: 260 }}
      />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <DataListColumnPicker model={pickerModel} onSave={save} registryReady={cfReady} />
        <DataListActionsMenu
          actions={[
            { key: 'add-contact', label: t('contacts.add_contact', { defaultValue: 'Add contact' }), icon: 'mingcute:add-line', onClick: addContact.onTrue },
            { key: 'bulk-import', label: t('contacts.bulk_import', { defaultValue: 'Bulk import' }), icon: 'solar:upload-bold', onClick: importContacts.onTrue, hidden: !canImport },
            ...contactActions.map((action, index) => ({ ...action, divider: index === firstVisibleAction })),
          ]}
        />
      </Stack>
    </Stack>
  );

  return (
    <>
      <Card sx={{ borderRadius: 0 }}>
        <DataList<IContactListItem>
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          isSearching={isSearching}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          sort={sortConfig}
          onSortChange={onSortChange}
          columnSearch={columnSearch}
          onColumnSearchChange={onColumnSearchChange}
          toolbar={toolbar}
          selectable
          selectedIds={selectedIds}
          onSelectRow={onSelectRow}
          onSelectAll={onSelectAll}
          renderRowLeading={renderRowLeading}
          renderRowActions={renderRowActions}
        />
      </Card>

      <RecordPanel type="contact" ids={rows.map((r) => r.id)} total={count} />

      <AddContactModal open={addContact.value} onClose={addContact.onFalse} />
      {contactActionModals}
      {canImport && <BulkContactUploadModal open={importContacts.value} onClose={importContacts.onFalse} />}
    </>
  );
}
